import { TFunction } from 'i18next';
import { WidgetsKindEnum } from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import WidgetFieldNames from '@lib/enums/fieldNames/widgetFieldNames';
import getTimeZones from '@lib/utils/getTimeZones';
import getChipTypesOptions from '@lib/components/Chip/utils/getChipTypesOptions';
import transformAttachmentForDuplication from 'routes/Home/utils/transformAttachmentForDuplication';

interface LocationValue {
  name: string;
  lat: number;
  lon: number;
}

interface ButtonItemValue {
  enabled?: boolean | null;
  updatedAt?: string | null;
}

interface ButtonsValue {
  task?: ButtonItemValue | null;
  intervention?: ButtonItemValue | null;
  formTemplate?: ButtonItemValue | null;
  asset?: ButtonItemValue | null;
  site?: ButtonItemValue | null;
  teamMember?: ButtonItemValue | null;
}

interface WidgetTemplateData {
  id: string;
  kind: WidgetsKindEnum;
  title?: string;
  description?: string;
  fields?: {
    backgroundColor?: string;
    content?: string;
    link?: string;
    timezone?: string;
    location?: string;
    onlyMy?: boolean;
    startAt?: string;
    endAt?: string;
    buttons?: ButtonsValue;
  };
  attachmentsAttached?: Array<{
    id?: string;
    filename?: string;
    urls?: {
      small?: string;
      medium?: string;
      original?: string;
    };
    fileData?: {
      url?: string;
      data?: Record<string, unknown>;
    };
  }>;
}

/**
 * Converts widget template data from select option to form values
 * Used to prefill form when a widget template is selected
 */
export default function getWidgetTemplateFormValues(
  templateData: WidgetTemplateData,
  t: TFunction<'translation', undefined>,
): Values {
  const values: Values = {};
  const { kind, fields = {} } = templateData;

  switch (kind) {
    case WidgetsKindEnum.ActiveInterventions: {
      values[WidgetFieldNames.ShowOnlyMyInterventions] = Boolean(fields.onlyMy);
      break;
    }

    case WidgetsKindEnum.Announcement: {
      // Message content
      if (fields.content) {
        values[WidgetFieldNames.AnnouncementMessageContent] = fields.content;
      }

      // Background color
      if (fields.backgroundColor) {
        const colorOptions = getChipTypesOptions({ t });
        const colorOption = colorOptions.find(
          (opt) => opt.value === fields.backgroundColor,
        );
        values[WidgetFieldNames.AnnouncementBoxColor] = colorOption || null;
      }

      // Schedule dates
      if (fields.startAt) {
        values[WidgetFieldNames.AnnouncementStartAt] = fields.startAt;
      }
      if (fields.endAt) {
        values[WidgetFieldNames.AnnouncementEndAt] = fields.endAt;
      }

      // Image attachment - transform for duplication
      if (
        templateData.attachmentsAttached &&
        templateData.attachmentsAttached.length > 0
      ) {
        const [firstAttachment] = templateData.attachmentsAttached;
        // Transform attachment from API format to form format for duplication
        // This allows the backend to create a copy of the file
        const transformedAttachment =
          transformAttachmentForDuplication(firstAttachment);
        if (transformedAttachment) {
          values[WidgetFieldNames.AnnouncementImage] = transformedAttachment;
        }
      }
      break;
    }

    case WidgetsKindEnum.Clock: {
      if (fields.timezone) {
        const timezoneOptions = getTimeZones();
        const timezoneOption = timezoneOptions.find(
          (opt) => opt.value === fields.timezone,
        );
        values[WidgetFieldNames.Timezone] = timezoneOption || null;
      }
      break;
    }

    case WidgetsKindEnum.ExternalLink: {
      if (fields.link) {
        values[WidgetFieldNames.ExternalUrl] = fields.link;
      }
      if (templateData.title) {
        values[WidgetFieldNames.ExternalLinkTitle] = templateData.title;
      }
      if (templateData.description) {
        values[WidgetFieldNames.ExternalLinkDescription] =
          templateData.description;
      }
      break;
    }

    case WidgetsKindEnum.WeatherToday: {
      if (fields.location) {
        try {
          const location = JSON.parse(fields.location) as LocationValue;
          values[WidgetFieldNames.WeatherTodayLocation] = location;
        } catch {
          // Invalid JSON, ignore
        }
      }
      break;
    }

    case WidgetsKindEnum.WeatherWeek: {
      if (fields.location) {
        try {
          const location = JSON.parse(fields.location) as LocationValue;
          values[WidgetFieldNames.WeatherWeekLocation] = location;
        } catch {
          // Invalid JSON, ignore
        }
      }
      break;
    }

    case WidgetsKindEnum.QuickCreation: {
      const { buttons } = fields;
      if (buttons) {
        values[WidgetFieldNames.QuickCreationTask] = Boolean(
          buttons.task?.enabled,
        );
        values[WidgetFieldNames.QuickCreationIntervention] = Boolean(
          buttons.intervention?.enabled,
        );
        values[WidgetFieldNames.QuickCreationFormTemplate] = Boolean(
          buttons.formTemplate?.enabled,
        );
        values[WidgetFieldNames.QuickCreationEquipment] = Boolean(
          buttons.asset?.enabled,
        );
        values[WidgetFieldNames.QuickCreationSite] = Boolean(
          buttons.site?.enabled,
        );
        values[WidgetFieldNames.QuickCreationTeamMember] = Boolean(
          buttons.teamMember?.enabled,
        );
      }
      break;
    }

    default:
      break;
  }

  return values;
}
